/**
 * Bus d'événements global — compatible Vue 3
 *
 * Vue 3 a supprimé $on / $off sur les instances Vue.
 * On s'appuie donc sur EventTarget (natif navigateur / Node ≥ 14)
 * pour fournir la même interface fire / listen / off que le pattern
 * popularisé par Laracasts (Component Communication #2 : Event Dispatcher).
 *
 * Initialisation (main.js) :
 *   import GlobalEvent from './services/event'
 *   window.Event = new GlobalEvent()
 *
 * Écouter depuis n'importe quel composant Vue :
 *   created() {
 *     Event.listen('my-event', (data) => this.myHandler(data))
 *   }
 *
 * Émettre depuis n'importe où :
 *   Event.fire('my-event', { foo: 'bar' })
 *
 * Se désabonner :
 *   Event.off('my-event', this.myHandler)
 *   // ou utiliser la fonction retournée par listen() :
 *   const unsubscribe = Event.listen('my-event', handler)
 *   unsubscribe()
 */
class GlobalEvent {
  constructor () {
    this._bus = new EventTarget()
  }

  /**
   * Émet un événement avec des données optionnelles.
   * @param {string} event  Nom de l'événement
   * @param {*}      data   Données transmises aux listeners
   */
  fire (event, data = null) {
    this._bus.dispatchEvent(
      new CustomEvent(event, { detail: data })
    )
  }

  /**
   * Enregistre un listener pour un événement.
   * Le callback reçoit directement `data` (pas l'objet CustomEvent brut).
   * @param {string}   event    Nom de l'événement
   * @param {Function} callback Fonction appelée avec les données
   * @returns {Function} Fonction d'unsubscribe
   */
  listen (event, callback) {
    // Wrapper pour extraire event.detail et passer data directement
    const handler = (e) => callback(e.detail)

    // Stocker le handler wrappé sur le callback original pour
    // pouvoir retrouver et supprimer le bon listener dans off()
    if (!callback._eventHandler) {
      callback._eventHandler = {}
    }
    callback._eventHandler[event] = handler

    this._bus.addEventListener(event, handler)

    // Retourne une fonction d'unsubscribe (cohérent avec l'API de apiStatusService)
    return () => this.off(event, callback)
  }

  /**
   * Supprime un listener précédemment enregistré avec listen().
   * @param {string}   event    Nom de l'événement
   * @param {Function} callback Le même callback passé à listen()
   */
  off (event, callback) {
    const handler = callback && callback._eventHandler && callback._eventHandler[event]
    if (handler) {
      this._bus.removeEventListener(event, handler)
      delete callback._eventHandler[event]
    }
  }
}

export default GlobalEvent
